import { Inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { BehaviorSubject, EMPTY, Observable } from 'rxjs'
import {
  IntegrationAuthData,
  MAIL_CHIMP_EVENT_HANDLER,
  MailChimpConfig,
  MailChimpListItem,
  MailChimpSyncRequest,
  SlackChannel,
  SlackConfigItem,
  ThirdPartyIntegrationIdentifies,
  PAPIAppModel,
} from './model'
import { deserializeArray, ENVIRONMENT, Toaster } from '@tokeet-frontend/tv3-platform'
import { capitalize, reduce } from 'lodash'
import { Store } from '@ngrx/store'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { ChekinHousingItem } from './chekin.model'
import { HoldedSettings, HoldedStatus, HoldedTestConnectionResponse, HoldedActivityLogRequest, HoldedActivityLogsResponse } from './holded.model'
import {
  QbDashboardResponse,
  QbStatusResponse,
  QbSettings,
  QbAccountResponse,
  QbExportObjectPayload,
  QbTestConnectionResponse,
  QbActivityLogRequest,
  QbActivityLogsResponse,
} from './quickbooks.model'
@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  private appCache = new Map<string, BehaviorSubject<PAPIAppModel>>()
  constructor(
    private http: HttpClient,
    private store: Store<any>,
    private toaster: Toaster,
    @Inject(ENVIRONMENT) private environment
  ) {}

  request(data) {
    const url = `@api/integration/request`
    return this.http.post(url, data, { responseType: 'arraybuffer' })
  }

  getAppInfo(id: string) {
    if (this.appCache.has(id)) {
      return this.appCache.get(id)
    }
    const sub = new BehaviorSubject<PAPIAppModel>(null)
    this.appCache.set(id, sub)

    return this.http.get<PAPIAppModel>(`@papi/auth/apps/${id}`).pipe(
      switchMap((app) => {
        sub.next(app)
        return sub
      })
    )
  }

  getAuthApps() {
    const url = `@papi/auth/apps`
    return this.http.get<{ total: number; data: PAPIAppModel[] }>(url).pipe(map((res) => res.data))
  }

  disconnectApp(appId: string) {
    const url = `@papi/auth/apps/${appId}`
    return this.http.delete(url)
  }

  getOAuth(integration: ThirdPartyIntegrationIdentifies): Observable<IntegrationAuthData> {
    const url = `@api/${integration}/oauth`
    return this.http.get(url).pipe(map(IntegrationAuthData.deserialize))
  }

  getConfig(integration: ThirdPartyIntegrationIdentifies): Observable<any> {
    const url = `@api/${integration}/config`
    return this.http.get(url)
  }

  unauthorize(integration: ThirdPartyIntegrationIdentifies): Observable<any> {
    const url = `@api/${integration}/oauth`
    return this.http
      .delete(url)
      .pipe(tap(() => this.toaster.success($localize`${capitalize(integration)} disconnected successfully`)))
  }

  connectWheelhouse(apiKey: string): Observable<any> {
    const url = `@api/wheelhouse/connect`
    return this.http.post(url, { wheelhouseUserApiKey: apiKey })
  }

  getWheelhouseStatus(): Observable<boolean> {
    const url = `@api/wheelhouse/status`
    return this.http.get<{ connected: boolean }>(url).pipe(map((res) => !!res?.connected))
  }

  disconnectWheelhouse(): Observable<any> {
    const url = `@api/wheelhouse/disconnect`
    return this.http.post(url, {})
  }

  connectChekin(accountType: string) {
    const url = `@api/v1/chekin/connect/`
    return this.http
      .post<{ response: { email?: string; password?: string; success: boolean } }>(url, { accountType })
      .pipe(map((res) => res.response))
  }

  getChekinStatus(): Observable<boolean> {
    const url = `@api/v1/chekin/status/`
    return this.http.get<{ response: { connected: boolean } }>(url).pipe(map((res) => !!res?.response?.connected))
  }

  disconnectChekin(): Observable<any> {
    const url = `@api/v1/chekin/disconnect/`
    return this.http.post(url, {})
  }

  getChekinHousings() {
    const url = `@api/v1/chekin/housings/`
    return this.http.get<{ response: ChekinHousingItem[] }>(url).pipe(map((res) => res.response))
  }

  connectChekinRental(rentalId: string): Observable<any> {
    const url = `@api/v1/checkin/rentals/${rentalId}/connect/`
    return this.http.post(url, {})
  }

  disconnectChekinRental(rentalId: string, deleteHousing: boolean = false): Observable<any> {
    const url = `@api/v1/checkin/rentals/${rentalId}/disconnect/`
    return this.http.post(url, { deleteHousing })
  }

  // #region Holded
  connectHolded(apiKey: string): Observable<any> {
    const url = `@api/holded/connect`
    return this.http.post(url, { apiKey })
  }

  getHoldedStatus(): Observable<boolean> {
    const url = `@api/holded/status`
    return this.http.get<{ connected: boolean }>(url).pipe(map((res) => !!res?.connected))
  }

  disconnectHolded(): Observable<any> {
    const url = `@api/holded/disconnect`
    return this.http.post(url, {})
  }

  getHoldedSettings(): Observable<HoldedStatus> {
    const url = `@api/holded/status`
    return this.http.get<HoldedStatus>(url)
  }

  updateHoldedSettings(payload: HoldedSettings): Observable<HoldedStatus> {
    const url = `@api/holded/settings`
    return this.http.put<HoldedStatus>(url, payload)
  }

  exportHoldedInvoices(invoicePkeys: string[]): Observable<any> {
    const url = `@api/holded/export/invoices`
    return this.http.post(url, { invoicePkeys })
  }

  testHoldedConnection(): Observable<HoldedTestConnectionResponse> {
    const url = `@api/holded/test-connection`
    return this.http.post<HoldedTestConnectionResponse>(url, {}).pipe(
      map((res) => { if (res.success) { return res } else { throw new Error(res.error) } })
    )
  }

  getHoldedActivityLogs(params: HoldedActivityLogRequest): Observable<HoldedActivityLogsResponse> {
    const url = `@api/holded/activity-logs`
    return this.http.get<HoldedActivityLogsResponse>(url, { params: params as any })
  }
  // #endregion

  // #region QuickBooks
  getQuickBooksDashboard() {
    const url = `@api/quickbooks/dashboard`
    return this.http.get<QbDashboardResponse>(url)
  }

  getQuickBooksStatus() {
    const url = `@api/quickbooks/status`
    return this.http.get<QbStatusResponse>(url)
  }

  updateQuickBooksSettings(payload: Partial<QbSettings>): Observable<QbStatusResponse> {
    const url = `@api/quickbooks/settings`
    return this.http.put<QbStatusResponse>(url, payload)
  }

  disconnectQuickBooks(): Observable<any> {
    const url = `@api/quickbooks/disconnect`
    return this.http.post(url, {})
  }

  getQuickBooksAccounts(): Observable<QbAccountResponse[]> {
    const url = `@api/quickbooks/accounts`
    return this.http.get<QbAccountResponse[]>(url)
  }

  exportTokeetData(payload: QbExportObjectPayload) {
    const url = `@api/quickbooks/tokeet/export`
    return this.http.post(url, payload)
  }

  testQuickBooksConnection() {
    const url = `@api/quickbooks/test-connection`
    return this.http.post<QbTestConnectionResponse>(url, {}).pipe(
      map((res) => {
        if (res.success) {
          return res
        } else {
          throw new Error(res.error)
        }
      })
    )
  }

  // /quickbooks/activity-logs
  getQuickBooksActivity(params: QbActivityLogRequest) {
    const url = `@api/quickbooks/activity-logs`
    return this.http.get<QbActivityLogsResponse>(url, { params: params as any })
  }

  // #endregion

  // #region MailChimp

  getMailChimpList(): Observable<MailChimpListItem[]> {
    const url = `@api/mailchimp/lists`
    return this.http.get(url).pipe(
      map((res) => res || []),
      deserializeArray<MailChimpListItem>(MailChimpListItem),
      catchError((error) => {
        this.store.dispatch(ActionFailed({ error }))
        return EMPTY
      })
    )
  }

  getMailChimpConfig(): Observable<{ [id: string]: MailChimpConfig }> {
    const url = `@api/mailchimp/config`
    return this.http.get(url).pipe(
      map((res) =>
        reduce(
          // @ts-ignore
          res.lists || {},
          (acc, item, id) => {
            acc[id] = MailChimpConfig.deserialize(item)
            return acc
          },
          {}
        )
      )
    )
  }

  updateMailChimpSync(payload: MailChimpSyncRequest) {
    const url = `@api/mailchimp/sync`
    return this.http.post(url, payload)
  }

  deleteMailChimpSync(mailchimpListId: string) {
    const url = `@api/mailchimp/unsync`
    return this.http.put(url, { handler: MAIL_CHIMP_EVENT_HANDLER, mailchimplist: mailchimpListId })
  }

  // #endregion

  // #region Slack
  getSlackChannels(): Observable<SlackChannel[]> {
    const url = `@api/slack/channels`
    return this.http.get(url).pipe(
      map((res) => res || []),
      deserializeArray<SlackChannel>(SlackChannel)
    )
  }

  getSlackConfig(): Observable<{ [id: string]: SlackConfigItem }> {
    const url = `@api/slack/config`
    return this.http.get(url).pipe(
      map((res) =>
        reduce(
          res,
          (acc, item, id) => {
            acc[id] = SlackConfigItem.deserialize(item)
            return acc
          },
          {}
        )
      )
    )
  }

  addSlackChannelConfig(payload) {
    const url = `@api/slack/add`
    return this.http.post(url, payload)
  }

  removeSlackChannelConfig(id) {
    const url = `@api/slack/remove/${id}`
    return this.http.delete(url)
  }

  // #endregion

  getConnectUrl(accountId: number, userId: string, integration: ThirdPartyIntegrationIdentifies): string {
    const state = `${accountId},${userId}`
    let url = ''
    switch (integration) {
      case ThirdPartyIntegrationIdentifies.Slack:
        const { slackOAuthUrl, slackClientId, slackRedirectUrl } = this.environment.config
        url = `${slackOAuthUrl}&scope=chat:write chat:write.public channels:read&client_id=${slackClientId}&state=${state}&redirect_uri=${encodeURIComponent(
          slackRedirectUrl
        )}`
        break
      case ThirdPartyIntegrationIdentifies.Mailchimp:
        const { mailChimpOAuthUrl, mailChimpClientId, mailChimpRedirectUrl } = this.environment.config
        url = `${mailChimpOAuthUrl}&client_id=${mailChimpClientId}&redirect_uri=${encodeURIComponent(
          mailChimpRedirectUrl + '/' + accountId
        )}`
        break
      case ThirdPartyIntegrationIdentifies.Dropbox:
        const { dropboxOAuthUrl, dropboxClientId, dropboxRedirectUrl } = this.environment.config
        url = `${dropboxOAuthUrl}&client_id=${dropboxClientId}&state=${state}&redirect_uri=${encodeURIComponent(
          dropboxRedirectUrl
        )}`
        break
      case ThirdPartyIntegrationIdentifies.Quickbooks:
        const { quickBooksOAuthUrl, quickBooksClientId, quickBooksRedirectUrl } = this.environment.config
        url = `${quickBooksOAuthUrl}&client_id=${quickBooksClientId}&state=${accountId}&redirect_uri=${encodeURIComponent(
          quickBooksRedirectUrl
        )}`
        break
      default:
        break
    }

    return url
  }
}
